import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Award, Users, GraduationCap, Heart, Mail, Phone, ArrowRight } from "lucide-react";

const STATS = [
    { value: "100+", label: "Successful Graduates" },
    { value: "97.5%+", label: "Student Satisfaction" },
    { value: "10+", label: "Career-Focused Programs" },
    { value: "PTIB", label: "Registered & Accredited" },
];

const PILLARS = [
    {
        icon: Users,
        title: "Personalized Attention",
        body: "Small online cohorts and a low student-to-instructor ratio so every learner is supported from admission to graduation.",
    },
    {
        icon: GraduationCap,
        title: "Career-Focused Programs",
        body: "Diploma and certificate pathways aligned with Canadian workplace standards — practical, hands-on, and job-ready.",
    },
    {
        icon: Heart,
        title: "Recognized Credentials",
        body: "PTIB-registered programs and industry-recognized certifications valued by employers across British Columbia and beyond.",
    },
];

export default function About() {
    return (
        <div className="bg-background" data-testid="about-page">
            {/* Hero */}
            <section className="max-w-6xl mx-auto px-6 pt-16 pb-10">
                <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-3">
                    About Us
                </div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-primary leading-[1.05]">
                    Welcome to Cambridge
                    <br />
                    College Online
                </h1>
                <p className="mt-6 text-lg text-foreground/75 max-w-2xl leading-relaxed">
                    Cambridge College Online is a career-focused post-secondary institution
                    delivering high-quality, industry-aligned diploma and certificate programs to
                    learners across Canada and around the world. We are dedicated to helping
                    students gain the skills, knowledge, and confidence they need to succeed in
                    their chosen field — through experienced instructors, supportive services,
                    and a flexible online learning environment.
                </p>
            </section>

            {/* Vision + Mission */}
            <section className="max-w-6xl mx-auto px-6 py-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="border-border">
                        <CardContent className="p-8">
                            <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-2">
                                Our Vision
                            </div>
                            <h2 className="font-display text-2xl text-primary mb-3">
                                A leader in career-focused diploma education
                            </h2>
                            <p className="text-foreground/80 leading-relaxed">
                                We aim to be a leader in diploma education, empowering students
                                with real-world expertise. Our vision is to create a thriving
                                community of lifelong learners who will grow and excel
                                professionally — wherever their careers take them.
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="border-border">
                        <CardContent className="p-8">
                            <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-2">
                                Our Mission
                            </div>
                            <h2 className="font-display text-2xl text-primary mb-3">
                                Practical training, supportive environment
                            </h2>
                            <p className="text-foreground/80 leading-relaxed">
                                At Cambridge College Online, we offer personalized, hands-on
                                training in a supportive environment. Our goal is to equip every
                                graduate with the practical skills and knowledge needed for a
                                successful, secure career.
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Stats band */}
            <section className="bg-primary text-primary-foreground py-14 my-10">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-2 text-center">
                        Cambridge By The Numbers
                    </div>
                    <h2 className="font-display text-3xl text-primary-foreground text-center mb-10">
                        A community built on outcomes
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-testid="about-stats">
                        {STATS.map((s) => (
                            <div key={s.label} className="text-center">
                                <div className="font-display text-4xl sm:text-5xl text-accent">
                                    {s.value}
                                </div>
                                <div className="text-sm text-primary-foreground/80 mt-2">
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pillars */}
            <section className="max-w-6xl mx-auto px-6 py-10">
                <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-3">
                    What we stand for
                </div>
                <h2 className="font-display text-3xl text-primary mb-8">
                    An education grounded in community
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {PILLARS.map(({ icon: Icon, title, body }) => (
                        <Card key={title} className="border-border">
                            <CardContent className="p-6">
                                <div className="h-10 w-10 rounded-full bg-accent/15 text-accent flex items-center justify-center mb-4">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <h3 className="font-display text-lg text-primary mb-2">{title}</h3>
                                <p className="text-sm text-foreground/75 leading-relaxed">{body}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-6xl mx-auto px-6 py-16">
                <div className="rounded-sm border border-border bg-secondary/30 p-10 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div>
                        <Award className="h-8 w-8 text-accent mb-3" />
                        <h3 className="font-display text-2xl text-primary">
                            Ready to begin your learning journey?
                        </h3>
                        <p className="text-foreground/75 mt-2">
                            Browse our online catalog or reach out — we're happy to help you choose
                            the right pathway.
                        </p>
                    </div>
                    <div className="flex gap-3 flex-wrap">
                        <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
                            <Link to="/" data-testid="about-cta-catalog">
                                Browse catalog
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Link>
                        </Button>
                        <Button asChild size="lg" variant="outline">
                            <Link to="/contact" data-testid="about-cta-contact">
                                Contact us
                            </Link>
                        </Button>
                    </div>
                </div>
                <div className="mt-6 flex items-center justify-center gap-6 text-sm text-muted-foreground">
                    <span className="inline-flex items-center gap-2">
                        <Phone className="h-4 w-4" /> +1 (604) 593-5400
                    </span>
                    <span className="inline-flex items-center gap-2">
                        <Mail className="h-4 w-4" /> contact@cambridgecollegeonline.com
                    </span>
                </div>
            </section>
        </div>
    );
}
